
import React, { useState, useEffect, useContext } from 'react';
import { GroupGame, defaultImageEdits, PageContextType, ImageEdits, Puzzle, Page } from '../types';
import ImageInput from '../components/ImageInput';
import { useAuth } from '../AuthContext';
import { PageContext } from '../PageContext';
import ConfirmationModal from '../components/ConfirmationModal';
import BackButton from '../components/BackButton';
import { usePuzzles } from '../PuzzleContext';

import BannerAd from '../components/BannerAd';

interface ManageGroupPageProps {
    groupId?: string;
}

type ConfirmAction = 
    | { type: 'deleteGroup' }
    | { type: 'kickMember', userId: string }
    | { type: 'resetScores' }
    | { type: 'deletePost', puzzleId: string };

const ManageGroupPage: React.FC<ManageGroupPageProps> = ({ groupId }) => {
    const { allUsers, userProfile, groups, updateGroup, deleteGroup, kickMember, resetGroupScores } = useAuth();
    const { puzzles, deletePuzzle } = usePuzzles();
    const pageContext = useContext<PageContextType | undefined>(PageContext);
    
    const group = groups.find(g => g.id === groupId);
    const groupPuzzles = puzzles.filter(p => p.groupId === groupId);

    // Form State
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [privacy, setPrivacy] = useState<'public' | 'private' | 'ask-to-join'>('public');
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [backgroundUrl, setBackgroundUrl] = useState<string | null>(null);
    const [admins, setAdmins] = useState<string[]>([]);
    const [imageEdits, setImageEdits] = useState<ImageEdits>(defaultImageEdits);
    const [backgroundEdits, setBackgroundEdits] = useState<ImageEdits>(defaultImageEdits);
    const [showLeaderboard, setShowLeaderboard] = useState<boolean>(true);
    
    // Confirmation State
    const [confirmState, setConfirmState] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        action: ConfirmAction | null;
        confirmText: string;
    }>({
        isOpen: false,
        title: '',
        message: '',
        action: null,
        confirmText: 'Confirm'
    });

    useEffect(() => {
        if (group) {
            setName(group.name);
            setDescription(group.description);
            setPrivacy(group.privacy);
            setImageUrl(group.imageUrl || null);
            setBackgroundUrl(group.backgroundUrl || null);
            setAdmins(group.admins || []);
            setImageEdits(group.imageEdits || defaultImageEdits);
            setBackgroundEdits(group.backgroundEdits || defaultImageEdits);
            setShowLeaderboard(group.showLeaderboard !== false); // Default to true if undefined
        }
    }, [group]);

    const handleSave = () => {
        if (!group) return;
        const updateData: any = { 
            name, 
            description, 
            privacy, 
            admins,
            imageEdits,
            backgroundEdits,
            showLeaderboard,
            imageUrl: imageUrl || null,
            backgroundUrl: backgroundUrl || null,
        };

        updateGroup(group.id, updateData);
        pageContext?.switchPage(Page.GameDetail, group.name, { gameId: group.id });
    };

    // --- Confirmation Handlers ---

    const handleDeleteGroupClick = () => {
        setConfirmState({
            isOpen: true,
            title: 'Delete Group?',
            message: 'Are you sure you want to delete this group forever? This cannot be undone.',
            action: { type: 'deleteGroup' },
            confirmText: 'Delete Group'
        });
    };

    const handleKickClick = (userId: string, userName: string) => {
        setConfirmState({
            isOpen: true,
            title: `Remove ${userName}?`,
            message: `Are you sure you want to remove ${userName} from the group?`,
            action: { type: 'kickMember', userId },
            confirmText: 'Remove'
        });
    };

    const handleResetScoresClick = () => {
        setConfirmState({
            isOpen: true,
            title: 'Reset Leaderboard?',
            message: "Everyone's score will be set to 0. This cannot be undone.",
            action: { type: 'resetScores' },
            confirmText: 'Reset Scores'
        });
    };

    const handleDeletePostClick = (puzzleId: string) => {
        setConfirmState({
            isOpen: true,
            title: 'Delete Post?',
            message: "Delete this post from the group? This action cannot be undone.",
            action: { type: 'deletePost', puzzleId },
            confirmText: 'Delete'
        });
    };

    const handleConfirmAction = async () => {
        const { action } = confirmState;
        if (!action || !group) return;

        switch (action.type) {
            case 'deleteGroup':
                try {
                    await deleteGroup(group.id);
                    pageContext?.switchPage(Page.Groups, 'Groups');
                } catch(e) {
                    console.error(e);
                    alert("Failed to delete group. Please try again.");
                }
                break;
            case 'kickMember':
                await kickMember(group.id, action.userId);
                break;
            case 'resetScores':
                await resetGroupScores(group.id);
                break;
            case 'deletePost':
                deletePuzzle(action.puzzleId);
                break;
        }
        setConfirmState(prev => ({ ...prev, isOpen: false }));
    };

    const toggleAdmin = (userId: string) => {
        if (!group || userId === group.creatorId) return; // Creator cannot be demoted
        setAdmins(prevAdmins => 
            prevAdmins.includes(userId) 
            ? prevAdmins.filter(id => id !== userId) 
            : [...prevAdmins, userId]
        );
    };

    if (!userProfile || !group) {
        return (
            <div className="space-y-4">
                 <div className="p-4"><BackButton text="Back to Groups" /></div>
                 <p className="text-center text-gray-500 py-8">Group not found.</p>
            </div>
        );
    }
    
    const members = allUsers.filter(u => group.members.includes(u.uid));
    const canDeleteGroup = userProfile.uid === group.creatorId || userProfile.isAdmin === true;

    return (
        <div className="space-y-6 pb-8 bg-gray-50 min-h-screen">
            {/* Dark Purple Header */}
            <div className="bg-[var(--primary-dark-purple)] text-white p-4 flex items-center justify-center sticky top-0 z-10 shadow-md rounded-b-2xl">
                <h2 className="text-xl font-bold aurora-text tracking-wider uppercase">Manage Group</h2>
            </div>

            <div className="space-y-6 px-4 pt-2">
                <button 
                    onClick={() => pageContext?.switchPage(Page.GameDetail, group.name, { gameId: group.id })}
                    className="flex items-center text-[var(--primary-dark-purple)] font-bold hover:underline"
                >
                    <i className="ph-bold ph-arrow-left mr-2 text-xl"></i> Back to Group
                </button>

                {/* Privacy Settings at Top */}
                <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">Privacy</label>
                     <div className="flex rounded-md shadow-sm border border-gray-300 overflow-hidden">
                        <button onClick={() => setPrivacy('public')} className={`flex-1 py-2 px-2 text-xs font-bold transition-colors ${privacy === 'public' ? 'bg-[var(--primary-dark-purple)] text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}>Public</button>
                        <button onClick={() => setPrivacy('private')} className={`flex-1 py-2 px-2 text-xs font-bold border-l border-r border-gray-300 transition-colors ${privacy === 'private' ? 'bg-[var(--primary-dark-purple)] text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}>Private</button>
                        <button onClick={() => setPrivacy('ask-to-join')} className={`flex-1 py-2 px-2 text-xs font-bold transition-colors ${privacy === 'ask-to-join' ? 'bg-[var(--primary-dark-purple)] text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}>Ask to Join</button>
                    </div>
                </div>

                {/* Group Details */}
                <div className="space-y-4 border-b border-gray-200 pb-4">
                    <ImageInput
                        label="Group Image"
                        imageUrl={imageUrl}
                        onUploadComplete={setImageUrl}
                        storagePath={`group-images/${userProfile.uid}`}
                        onLaunchEditor={(url) => pageContext?.launchImageEditor('post', url || imageUrl!, imageEdits, 'square', setImageEdits, null)}
                        imageEdits={imageEdits}
                        previewShape="square"
                    />
                    <ImageInput
                        label="Group Background"
                        imageUrl={backgroundUrl}
                        onUploadComplete={setBackgroundUrl}
                        storagePath={`group-images/${userProfile.uid}`}
                        onLaunchEditor={(url) => pageContext?.launchImageEditor('post', url || backgroundUrl!, backgroundEdits, 'square', setBackgroundEdits, null)}
                        imageEdits={backgroundEdits}
                        previewShape="square"
                    />
                    <div>
                        <label htmlFor="groupName" className="block text-sm font-medium text-gray-700">Group Name</label>
                        <input type="text" id="groupName" value={name} onChange={e => setName(e.target.value)} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-[var(--accent-pink)] focus:border-[var(--accent-pink)]" />
                    </div>
                    <div>
                        <label htmlFor="groupDesc" className="block text-sm font-medium text-gray-700">Description</label>
                        <textarea id="groupDesc" value={description} onChange={e => setDescription(e.target.value)} rows={2} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-[var(--accent-pink)] focus:border-[var(--accent-pink)]" />
                    </div>
                </div>

                {/* Leaderboard Toggle */}
                <div className="flex items-center justify-between border-b border-gray-200 pb-4">
                    <div>
                        <h3 className="text-lg font-bold text-gray-900">Enable Leaderboard</h3>
                        <p className="text-sm text-gray-500">Show a leaderboard for this group.</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" checked={showLeaderboard} onChange={(e) => setShowLeaderboard(e.target.checked)} />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                </div>

                {/* Member Management */}
                 <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Members ({members.length})</h3>
                    <div className="space-y-2 border border-gray-200 p-2 rounded-md bg-white max-h-60 overflow-y-auto custom-scrollbar">
                       {members.map(member => (
                           <div key={member.uid} className="flex items-center justify-between p-2 rounded-md hover:bg-gray-50 border-b border-gray-100 last:border-0">
                               <div className="flex items-center gap-2 overflow-hidden">
                                   <img src={member.photoURL} alt={member.displayName} className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                                   <span className="text-sm font-semibold truncate text-gray-800">{member.displayName}</span>
                                   {group.creatorId === member.uid && <span className="text-[10px] bg-yellow-100 text-yellow-800 px-1 rounded border border-yellow-200">Owner</span>}
                                   {admins.includes(member.uid) && member.uid !== group.creatorId && <span className="text-[10px] bg-blue-100 text-blue-800 px-1 rounded border border-blue-200">Admin</span>}
                               </div>
                               <div className="flex items-center gap-2">
                                   <button 
                                       onClick={() => toggleAdmin(member.uid)}
                                       disabled={group.creatorId === member.uid}
                                       className={`px-2 py-1 text-[10px] font-bold rounded transition-colors ${admins.includes(member.uid) ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} disabled:opacity-50`}
                                   >
                                       {admins.includes(member.uid) ? 'Demote' : 'Promote'}
                                   </button>
                                   {member.uid !== group.creatorId && (
                                        <button 
                                            onClick={() => handleKickClick(member.uid, member.displayName)}
                                            className="w-6 h-6 flex items-center justify-center bg-red-100 hover:bg-red-200 rounded text-red-600 transition-colors"
                                            title="Kick Member"
                                        >
                                            <i className="ph-bold ph-boot"></i>
                                        </button>
                                   )}
                               </div>
                           </div>
                       ))}
                    </div>
                </div>

                {/* Content & Actions */}
                <div className="space-y-4">
                    <h3 className="text-lg font-bold text-gray-900">Content & Actions</h3>
                    
                    {showLeaderboard && (
                        <button onClick={handleResetScoresClick} className="w-full bg-orange-100 hover:bg-orange-200 text-orange-700 font-bold py-2 px-4 rounded-lg border border-orange-200 flex items-center justify-center gap-2">
                            <i className="ph-bold ph-arrow-counter-clockwise"></i> Reset Leaderboard
                        </button>
                    )}

                    <div className="border border-gray-200 rounded-md bg-white p-2">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Manage Posts ({groupPuzzles.length})</h4>
                        <div className="max-h-60 overflow-y-auto custom-scrollbar space-y-2">
                            {groupPuzzles.length > 0 ? (
                                groupPuzzles.map(puzzle => (
                                    <div key={puzzle.id} className="flex items-center justify-between bg-gray-50 p-2 rounded border border-gray-100">
                                        <div className="flex items-center gap-2 overflow-hidden">
                                            <img src={puzzle.imageUrl} className="w-8 h-8 object-cover rounded flex-shrink-0" />
                                            <span className="text-xs truncate text-gray-700">{puzzle.clue || '(No Clue)'}</span>
                                        </div>
                                        <button onClick={() => handleDeletePostClick(puzzle.id)} className="text-red-500 hover:text-red-700 p-1">
                                            <i className="ph-bold ph-trash"></i>
                                        </button>
                                    </div>
                                ))
                            ) : (
                                <p className="text-xs text-gray-500 text-center py-2">No posts yet.</p>
                            )}
                        </div>
                    </div>

                    {canDeleteGroup && (
                        <button onClick={handleDeleteGroupClick} className="w-full bg-red-100 hover:bg-red-200 text-red-700 font-bold py-3 px-4 rounded-lg border border-red-200 mt-4">
                            Delete Group
                        </button>
                    )}
                </div>
            </div>
            
            <div className="flex justify-end space-x-2 pt-6 border-t border-gray-200 mt-6 px-4">
                <button onClick={() => pageContext?.switchPage(Page.GameDetail, group.name, { gameId: group.id })} className="bg-transparent border border-gray-300 text-gray-700 font-bold py-2 px-4 rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={handleSave} className="bg-gradient-to-r from-pink-500 to-yellow-500 text-white font-bold py-2 px-6 rounded-lg shadow-lg hover:opacity-90">Save Changes</button>
            </div>
            
            <div className="mt-8 flex justify-center">
                <BannerAd />
            </div>
            
            <ConfirmationModal
                isOpen={confirmState.isOpen}
                onClose={() => setConfirmState(prev => ({ ...prev, isOpen: false }))}
                onConfirm={handleConfirmAction}
                title={confirmState.title}
                message={confirmState.message}
                confirmText={confirmState.confirmText}
                triggerRect={null}
            />
        </div>
    );
};

export default ManageGroupPage;
